import { useEffect, useMemo, useState } from 'react';
import FMDashboardService, { IFMDashboardService } from '../../../services/FMDashboard.service';
import DSEService, { IDSEService } from '../../../services/DSE.service';
import FMOneProfileService, { IFMOneProfileService } from '../../../services/FMOneProfile.service';
import { getCurrentManagerAlias } from '../shared/fmUser';
import { mapTeamCalendarToRows } from '../FMTeamCalendar/fmTeamCalendarMap';
import { mergeSkillsIntoRows } from '../shared/fmSkillsMerge';
import { ResourceSkills } from '../../../contracts/FMOneProfileContracts';
import { FMForecastRow } from '../FMForecastView/FMForecastView';
import { EventTypeOption, TechnologyOption } from '../../../contracts/FMPendingContracts';
import { GrmEventType } from '../../../contracts/FMGrmContracts';
import { ExportResource } from '../shared/exportForecast';
import {
  fmSampleDays,
  fmSampleEventTypes,
  fmSampleExportResources,
  fmSampleForecastRows,
  fmSampleMonth,
} from '../fmSampleData';
import { getDays } from '../FMTeamCalendar/fmForecastLayout';
import { ForecastDay } from '../../../contracts/FMForecastContracts';

const pad = (n: number): string => String(n).padStart(2, '0');
const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const MONTHS3 = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** Map GRM master-data event types → the UI `EventTypeOption` used by the legend + add-event form. */
const toEventTypeOptions = (types: GrmEventType[]): EventTypeOption[] =>
  types
    .filter((t) => t.EventTypeName)
    .map((t) => ({
      EventTypeId: t.EventTypeId,
      EventTypeName: t.EventTypeName,
      EventColor: t.EventColor,
      IsCommentRequired: t.IsCommentRequired,
      IsBusy: t.IsBusy,
      IsSpecialtyRequired: t.IsSpecialtyRequired,
    }));

const toManagerAddEventTypes = (types: GrmEventType[]): EventTypeOption[] =>
  toEventTypeOptions(
    types.filter(
      (type) =>
        type.IsActive === true &&
        type.DisplayInEventList === true &&
        type.IsApplicableToManagers === true
    )
  );

export type FMDataSource = 'live' | 'sample' | 'unavailable';

export type DSECapacityReadStatus = 'loaded' | 'missing' | 'error';

export interface DSECapacityReadState {
  status: DSECapacityReadStatus;
  message?: string;
}

export interface FMDashboardData {
  rows: FMForecastRow[];
  days: ForecastDay[];
  /** All event types used by the calendar legend/filter. */
  eventTypes: EventTypeOption[];
  /** Active event types managers are allowed to create. */
  addEventTypes: EventTypeOption[];
  /** Primary technologies (from GRM master data) for the Add-Event form's Technology dropdown. */
  technologies: TechnologyOption[];
  monthLabel: string;
  monthStart: Date;
  /** Raw team-calendar resources for the CSV export. */
  exportResources: ExportResource[];
  /** The manager alias the data was actually loaded for. */
  managerAlias?: string;
  loading: boolean;
  /** Sample data is only enabled explicitly for component tests/local preview. */
  source: FMDataSource;
  /** Non-null when live data could not be loaded. */
  error?: string;
  /** Per-resource result of the authoritative G600 DSE capacity-hours read. */
  capacityReadByAlias: Record<string, DSECapacityReadState>;
  /** Reload token whose response is represented by this state. */
  loadedReloadToken: number;
}

export interface UseFMDashboardDataOptions {
  /** Injected for tests; defaults to the FXP-backed singleton. */
  service?: IFMDashboardService;
  /** Injected for tests; defaults to the FXP-backed singleton. Supplies row Capacity Hours (F7). */
  dseService?: IDSEService;
  /** Injected for tests; defaults to the FXP-backed singleton. Supplies profile/skill columns (F2). */
  oneProfileService?: IFMOneProfileService;
  /** Override the manager alias; defaults to the OBO-aware current user. */
  managerAlias?: string;
  /** First day of the month to load; defaults to the current month. */
  monthStart?: Date;
  /** Force sample data (e.g. local preview without GRM access). */
  useSample?: boolean;
  /** Bump to force a re-fetch (e.g. after adding an event). */
  reloadToken?: number;
}

const sampleData = (
  monthStart?: Date
): Omit<
  FMDashboardData,
  | 'loading'
  | 'source'
  | 'error'
  | 'managerAlias'
  | 'capacityReadByAlias'
  | 'loadedReloadToken'
> => {
  // Sample ROWS are fixed, but the month header/columns still track the requested month so month
  // navigation is visibly live in the local loop (and not silently dead when we fall back).
  const month = monthStart ?? fmSampleMonth;
  const isSampleMonth =
    month.getFullYear() === fmSampleMonth.getFullYear() && month.getMonth() === fmSampleMonth.getMonth();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return {
    rows: fmSampleForecastRows,
    days: isSampleMonth ? fmSampleDays : getDays(month, daysInMonth),
    eventTypes: fmSampleEventTypes,
    addEventTypes: fmSampleEventTypes,
    technologies: [],
    monthLabel: `${MONTHS3[month.getMonth()]} ${month.getFullYear()}`,
    monthStart: month,
    exportResources: fmSampleExportResources,
  };
};

const emptyData = (
  monthStart: Date
): Omit<
  FMDashboardData,
  | 'loading'
  | 'source'
  | 'error'
  | 'managerAlias'
  | 'capacityReadByAlias'
  | 'loadedReloadToken'
> => {
  const daysInMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0
  ).getDate();
  return {
    rows: [],
    days: getDays(monthStart, daysInMonth),
    eventTypes: [],
    addEventTypes: [],
    technologies: [],
    monthLabel: `${MONTHS3[monthStart.getMonth()]} ${monthStart.getFullYear()}`,
    monthStart,
    exportResources: [],
  };
};

/**
 * Loads the FM team forecast from the live GRM API (GetTeamCalendar + GetCalendarMasterData),
 * and maps it to `FMForecastView` rows. Sample data is available only through the explicit
 * `useSample` test/local-preview option; live failures are surfaced without fabricated resources.
 */
export function useFMDashboardData(options: UseFMDashboardDataOptions = {}): FMDashboardData {
  // Stable month-start: keyed on the provided time value (or undefined) so the default month is
  // computed once and doesn't re-trigger the effect every render.
  const monthStartMs = options.monthStart ? options.monthStart.getTime() : undefined;
  const monthStart = useMemo(() => {
    if (options.monthStart) {
      return options.monthStart;
    }
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStartMs]);
  const reloadToken = options.reloadToken ?? 0;
  const [state, setState] = useState<FMDashboardData>(() =>
    options.useSample
      ? {
          ...sampleData(monthStart),
          loading: false,
          source: 'sample',
          capacityReadByAlias: {},
          loadedReloadToken: reloadToken,
        }
      : {
          ...emptyData(monthStart),
          loading: true,
          source: 'unavailable',
          capacityReadByAlias: {},
          loadedReloadToken: reloadToken,
        }
  );

  useEffect(() => {
    let cancelled = false;

    if (options.useSample) {
      setState({
        ...sampleData(monthStart),
        loading: false,
        source: 'sample',
        capacityReadByAlias: {},
        loadedReloadToken: reloadToken,
      });
      return;
    }

    const alias = options.managerAlias ?? getCurrentManagerAlias();
    if (!alias) {
      setState({
        ...emptyData(monthStart),
        loading: false,
        source: 'unavailable',
        error: 'Unable to determine the current manager from the FXP user context.',
        capacityReadByAlias: {},
        loadedReloadToken: reloadToken,
      });
      return;
    }

    const service = options.service ?? FMDashboardService.getSingleton();
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const monthLabel = `${MONTHS3[monthStart.getMonth()]} ${monthStart.getFullYear()}`;
    const days = getDays(monthStart, monthEnd.getDate());

    setState({
      ...emptyData(monthStart),
      managerAlias: alias,
      loading: true,
      source: 'unavailable',
      capacityReadByAlias: {},
      loadedReloadToken: reloadToken,
    });

    (async () => {
      try {
        const [master, calendar] = await Promise.all([
          service.getCalendarMasterData(),
          service.getTeamCalendar({
            StartDate: ymd(monthStart),
            EndDate: ymd(monthEnd),
            ManagerAlias: alias,
          }),
        ]);
        if (cancelled) {
          return;
        }
        const grmTypes = master?.DomainDataResult?.EventTypes ?? [];
        const eventTypes = toEventTypeOptions(grmTypes);
        const addEventTypes = toManagerAddEventTypes(grmTypes);
        const technologies: TechnologyOption[] =
          master?.DomainDataResult?.PrimaryTechnologies ?? [];
        const rows = mapTeamCalendarToRows(calendar, { eventTypes: grmTypes, monthStart });
        if (rows.length === 0) {
          setState({
            rows: [],
            days,
            eventTypes,
            addEventTypes,
            technologies,
            monthLabel,
            monthStart,
            exportResources: [],
            managerAlias: alias,
            loading: false,
            source: 'live',
            capacityReadByAlias: {},
            loadedReloadToken: reloadToken,
          });
          return;
        }
        // F7: the team calendar carries no Capacity Hours — they come from the RAvail DSE API.
        // Without this the whole Capacity Hours column renders 0 and the DSE editor opens empty.
        // Non-fatal: a missing/failing DSE service must not downgrade the team calendar to sample.
        const capacityResult = await (async (): Promise<{
          hoursByAlias: Record<string, number>;
          readByAlias: Record<string, DSECapacityReadState>;
        }> => {
          const rowAliases = rows.map((row) => row.rowId.toLowerCase());
          try {
            const dse = options.dseService ?? DSEService.getSingleton();
            const resp = await dse.getDSECapacityHours(rows.map((r) => r.rowId));
            if (resp?.responseCode !== 'G600') {
              throw new Error(
                resp?.statusMessage ||
                  'Capacity-hours service did not return a successful G600 response.'
              );
            }
            const hoursByAlias: Record<string, number> = {};
            for (const rec of resp?.records ?? []) {
              const recordAlias = (rec.resourceAlias || '').toLowerCase();
              if (
                recordAlias &&
                typeof rec.contractHours === 'number' &&
                isFinite(rec.contractHours)
              ) {
                hoursByAlias[recordAlias] = rec.contractHours;
              }
            }
            const readByAlias: Record<string, DSECapacityReadState> = {};
            rowAliases.forEach((rowAlias) => {
              readByAlias[rowAlias] =
                hoursByAlias[rowAlias] !== undefined
                  ? { status: 'loaded' }
                  : {
                      status: 'missing',
                      message:
                        'Capacity hours were not returned for this resource.',
                    };
            });
            return { hoursByAlias, readByAlias };
          } catch (error) {
            const detail =
              error instanceof Error
                ? error.message
                : 'Failed to load capacity hours.';
            const readByAlias: Record<string, DSECapacityReadState> = {};
            rowAliases.forEach((rowAlias) => {
              readByAlias[rowAlias] = {
                status: 'error',
                message: detail,
              };
            });
            return { hoursByAlias: {}, readByAlias };
          }
        })();
        if (cancelled) {
          return;
        }
        const rowsWithHours = rows.map((r) => {
          const rowAlias = r.rowId.toLowerCase();
          const h = capacityResult.hoursByAlias[rowAlias];
          if (h !== undefined) {
            return { ...r, contractHours: h };
          }
          // G600 with no record is an authoritative "not created yet" result. Legacy displays 0
          // and lets the manager create the record through the upsert endpoint.
          return capacityResult.readByAlias[rowAlias]?.status === 'missing'
            ? { ...r, contractHours: 0 }
            : r;
        });
        // F2: Profile Completion % / Skill Assessment % / Skills Last Updated come from the
        // OneProfile OpGraphQL API (NOT the team calendar), one query per team alias — parity of
        // GetDirectReporteesSkills. Non-fatal: a missing/failing OneProfile must not downgrade to
        // sample or lose the calendar; those columns just stay 0/NA.
        const skillsByAlias = await (async (): Promise<Record<string, ResourceSkills>> => {
          try {
            const op = options.oneProfileService ?? FMOneProfileService.getSingleton();
            const results = await Promise.all(
              rows.map((r) => op.getResourceSkills(r.rowId).catch(() => undefined))
            );
            const map: Record<string, ResourceSkills> = {};
            results.forEach((res) => {
              if (res) {
                map[res.alias] = res;
              }
            });
            return map;
          } catch {
            return {};
          }
        })();
        if (cancelled) {
          return;
        }
        const rowsFinal = mergeSkillsIntoRows(rowsWithHours, skillsByAlias);
        const calendarItems = calendar?.TeamCalendarItems ?? {};
        setState({
          rows: rowsFinal,
          days,
          eventTypes,
          addEventTypes,
          technologies,
          monthLabel,
          monthStart,
          exportResources: Object.keys(calendarItems).map((a) => calendarItems[a]),
          managerAlias: alias,
          loading: false,
          source: 'live',
          capacityReadByAlias: capacityResult.readByAlias,
          loadedReloadToken: reloadToken,
        });
      } catch (e) {
        if (cancelled) {
          return;
        }
        setState({
          ...emptyData(monthStart),
          managerAlias: alias,
          loading: false,
          source: 'unavailable',
          error: e instanceof Error ? e.message : 'Failed to load team calendar',
          capacityReadByAlias: {},
          loadedReloadToken: reloadToken,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    options.service,
    options.dseService,
    options.oneProfileService,
    options.managerAlias,
    options.useSample,
    reloadToken,
    monthStart,
  ]);

  return state;
}
